import React, { useContext, useEffect, useRef } from "react";
import { SocketContext } from "context/SocketContext";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "hooks/useSnackbar";
import { playSound } from "utils";
import store, { useAppDispatch } from "store";
import { resetSnackbar } from "store/commonSlice";
import { prependNotification, setUnreadCount, pushToast } from "store/notificationsSlice";
import { INotificationItem, INotificationParams, INotificationTarget, NotificationStatus } from "api/notifications/notifications.type";
import { DESIGN_TYPES_ARRAY } from "pages/DesignPage/DesignProvider";
import { reportError, FeatureArea } from "utils/sentry";
import { trackEvent } from "utils/analytics";
import { EVENTS } from "utils/analytics.events";
import { getFilenameFromUrl, isIosDevice, openInNewTab } from "components/DownloadButton/utils";
import { dispatchIos4kReady } from "components/DownloadButton/ios4kSave";
import i18n from "i18n";
import { PATHS } from "router/config/paths";
import { httpStatusOf } from "utils/httpStatusOf";
import { consumeOwnedDownload } from "components/SocketListener/pendingDownloads";

export const EventSvgDownloadComplete = "RefabricSvgDownloadComplete";

// Fired when a "Download as 4K" upscale job finishes (success or failure) so any open
// "Enhancing resolution" popup resets. Mirrors EventSvgDownloadComplete.
export const EventUpscaleComplete = "RefabricUpscaleComplete";

interface SocketListenerProps {
  children: React.ReactNode;
}

type MessageType =
  | "design"
  | "magic_selection"
  | "notification"
  | "design_status_update"
  | "training_progress"
  | "session_expired"
  | "maintenance"
  | "payment_required"
  | "collaboration_update"
  | "get_vector_file"
  | "upscale_image"
  | "error"
  | "mannequin_photoshoot"
  | "photoshooting_mannequin"
  | "ghost_shooting"
  | "tryon"
  | "design_canvas_frame"
  | "connected"
  | "chatbot_chunk"
  | "feedback_resolved"
  | "studio_project"
  | "studio_polish";

type NotificationSeverity = "success" | "error" | "warning" | "info";

interface SocketMessage {
  design_detail?: any;
  design_type?: MessageType;
  type?: string;
  status?: number | string;
  content?: string;
  severity?: NotificationSeverity;
  progress?: number;
  action?: string;
  userName?: string;
  error?: string;
  error_message?: string;
  error_type?: string;
  timestamp?: number;
  design_id?: string;
  project_id?: string;
  /** REF-6367 — `polish_edits` (per frame) or `polish_batch` (settlement). */
  edit_type?: string;
  /** REF-6878 — the polish batch's id; the settlement's occurrence identity. */
  batch_id?: string;
  batch_status?: string;
  batch_progress?: { completed?: number; failed?: number; cancelled?: number; total?: number; credit_charged?: number };
  train_id?: string;
  training_id?: string;
  vector_url?: string;
  image_url?: string;
  file_name?: string;
  message?: string;
  session_id?: string;
  // Generation-completion Notifications (REF-6306). Any terminal frame — design/photoshoot/studio/etc. —
  // can additionally carry this key. It is independent of `design_type`/`type` above and handled
  // additively (see handleGenerationNotification), never routed through MESSAGE_HANDLERS.
  notification?: {
    id: string;
    /** Truthy → show a toast. The BE sends a boolean (REF-6607 mentions) or a surface name. */
    surface?: string | boolean;
    persist?: boolean;
    title_key: string;
    params: INotificationParams;
    target: INotificationTarget;
    status: NotificationStatus;
    success_count: number;
    failure_count: number;
    cover_url?: string | null;
  };
}

interface MessageHandlerContext {
  navigate: ReturnType<typeof useNavigate>;
  dispatch: ReturnType<typeof useAppDispatch>;
  showSuccess: (message: string, options?: any) => void;
  showError: (message: string, options?: any) => void;
  showInfo: (message: string, options?: any) => void;
  showWarning: (message: string, options?: any) => void;
  setSessionId?: (sessionId: string) => void;
  isDesignCompleted?: (designId: string) => boolean;
}

// Helper to truncate long messages
const truncateMessage = (message: string, maxLength = 200): string => {
  if (message.length <= maxLength) return message;

  return message.substring(0, maxLength) + "...";
};

// Map a generation socket message's design_type to the right user-facing copy
// and Sentry featureArea. Keeps HTTP/WS reporting buckets consistent with
// inferFeatureArea (mannequin + ghost both bucket under "photoshoot").
const getGenerationErrorContext = (designType?: string): { label: string; featureArea: FeatureArea } => {
  switch (designType) {
    case "tryon":
      return { label: i18n.t("snackbar:design_type_error.tryon"), featureArea: "tryon" };
    case "mannequin_photoshoot":
    case "photoshooting_mannequin":
      return { label: i18n.t("snackbar:design_type_error.mannequin"), featureArea: "photoshoot" };
    case "ghost_shooting":
      return { label: i18n.t("snackbar:design_type_error.ghost"), featureArea: "photoshoot" };
    case "design_canvas_frame":
      return { label: i18n.t("snackbar:design_type_error.design_canvas_frame"), featureArea: "design-canvas" };
    default:
      return { label: i18n.t("snackbar:design_type_error.generic"), featureArea: "photoshoot" };
  }
};

// Helper to get appropriate error message based on error_type
const getErrorMessage = (message: SocketMessage, defaultMessage: string): string => {
  let errorMessage: string;

  // If error_type exists and is content_policy_violation, use the error field directly
  if (message.error_type === "content_policy_violation") {
    errorMessage = message.error_message || message.error || message.message || "Content policy violation detected";
  } else {
    // For general_error or missing error_type, use error_message / message / error fields or default
    errorMessage = message.error_message || message.message || message.error || defaultMessage;
  }

  return truncateMessage(errorMessage);
};

// content_policy_violation is expected user feedback (NSFW prompts etc.) — never report.
const reportSocketError = (message: SocketMessage, errorMessage: string, featureArea: FeatureArea) => {
  if (message.error_type === "content_policy_violation") return;

  reportError(new Error(errorMessage), {
    source: "websocket",
    featureArea,
    level: "error",
    // Group by feature + design_type + error_type so generation failures form distinct,
    // trackable Sentry issues instead of collapsing into one bucket keyed on the
    // backend-controlled error string (REFABRIC-FRONTEND-3Q merged tryon/photoshoot/design-canvas).
    fingerprint: ["ws-generation-error", featureArea, message.design_type ?? "unknown", message.error_type ?? "general_error"],
    extras: {
      error_type: message.error_type,
      design_id: message.design_id,
      train_id: message.train_id ?? message.training_id,
      session_id: message.session_id,
      timestamp: message.timestamp,
      socket_type: message.type,
      design_type: message.design_type,
    },
  });
};

// Helper to check and mark message as processed
const shouldProcessMessage = (
  message: SocketMessage,
  prefix: string,
  context: MessageHandlerContext & { isMessageProcessed?: (id: string) => boolean; markMessageAsProcessed?: (id: string) => void }
): boolean => {
  const messageId = `${prefix}_${message.timestamp || Date.now()}`;

  if (context.isMessageProcessed?.(messageId)) {
    if (process.env.NODE_ENV === "development") {
      console.log(`[SocketListener] [${prefix}] Message already processed, skipping:`, messageId);
    }

    return false;
  }

  context.markMessageAsProcessed?.(messageId);

  return true;
};

// REF-6878 (Phase D · D3b): a completion toast fires once per OCCURRENCE, keyed on the identity the BE sends for
// it, never on the client receive timestamp. The manager stamps `timestamp = Date.now()` on receive, so the old
// `<prefix>_<timestamp>` key let a replayed / re-drained / resent copy toast again and dropped a second, distinct
// completion that landed in the same millisecond.
// - design / pattern / DESIGN_TYPES_ARRAY and the photoshoot family: `design_type:design_id:<terminal kind>`. The
//   design_id is fresh per dispatch (a regenerate is a new id), so a repeat of the same id + kind is a copy of the
//   same occurrence, with or without `replayed`. Success and failure are distinct kinds: the BE's markFailed is
//   unconditional, so a failure after a success for the same id still shows.
// - Studio polish settlement: `polish_batch:batch_id` (one settlement per batch, single-winner on the BE).
// Only terminal frames claim: progress (`success/0`) and other kinds return before reaching a claim.
// Module-scoped, like `rememberNotificationId` below and for the same reason: `SocketListener` is re-instantiated
// per route while the socket (and its replays) is not, so a per-instance latch would toast a replay again after a
// route change. Bounded, private, and holds nothing but these ids.
const MAX_SETTLED_OCCURRENCES = 500;
const settledOccurrences = new Set<string>();
const claimOccurrence = (key: string): boolean => {
  if (settledOccurrences.has(key)) return false;
  settledOccurrences.add(key);
  if (settledOccurrences.size > MAX_SETTLED_OCCURRENCES) {
    const oldest = settledOccurrences.values().next().value as string | undefined;
    if (oldest !== undefined) settledOccurrences.delete(oldest);
  }

  return true;
};

type HandlerContextWithDedup = MessageHandlerContext & { isMessageProcessed?: (id: string) => boolean; markMessageAsProcessed?: (id: string) => void };

// A frame without a design_id carries no confirmed identity: it keeps the legacy prefix guard (unchanged, AC5).
const shouldToastTerminal = (message: SocketMessage, kind: "success" | "failed", legacyPrefix: string, context: HandlerContextWithDedup): boolean =>
  message.design_id ? claimOccurrence(`${message.design_type}:${message.design_id}:${kind}`) : shouldProcessMessage(message, legacyPrefix, context);

// Message handler functions
const handleDesignMessage = (message: SocketMessage, context: HandlerContextWithDedup) => {
  // Handle error status (99)
  if (message.status === 99 || message.type === "failed") {
    if (!shouldToastTerminal(message, "failed", "design", context)) {
      return;
    }

    const errorMessage = getErrorMessage(message, "An error occurred during design generation");
    context.showError(errorMessage);
    reportSocketError(message, errorMessage, "design-canvas");

    return;
  }

  if (message.type === "success" && message.status === 1) {
    if (!shouldToastTerminal(message, "success", "design", context)) {
      return;
    }

    // Only show notification if not on design page (DesignStatusListener will handle it there)
    if (!location.pathname.includes("design")) {
      // REF-6306: beep now centralized in handleGenerationNotification for design_types the
      // notification registry covers ("design"/"txt2img" persist=True) — don't double-beep here.
      context.showSuccess("Design is generated successfully.");
    }
  }
};

const handlePhotoshootMessage = (message: SocketMessage, context: HandlerContextWithDedup) => {
  // Handle error status (99)
  if (message.status === 99 || message.type === "failed") {
    if (!shouldToastTerminal(message, "failed", "photoshoot", context)) {
      return;
    }

    const { label, featureArea } = getGenerationErrorContext(message.design_type);
    const errorMessage = getErrorMessage(message, label);
    context.showError(errorMessage);
    reportSocketError(message, errorMessage, featureArea);

    return;
  }

  if (message.type === "success" && message.status === 1) {
    if (!shouldToastTerminal(message, "success", "photoshoot", context)) {
      return;
    }

    // Skip if already handled locally (e.g., studio edit modal with handleSocketResult)
    if (message.design_id && context.isDesignCompleted?.(message.design_id)) {
      return;
    }

    // Check if user is on the same photoshoot page or studio edit page that completed
    const currentPhotoshootId = location.pathname.match(/\/photoshoot\/([^/]+)/)?.[1];
    const isOnSamePhotoshoot = currentPhotoshootId && currentPhotoshootId === message.design_id;
    const isOnStudioDetailPage = /\/photoshoots\/studio\/[^/]+/.test(location.pathname);

    // Skip notification if on same legacy /photoshoot/:id page (now a redirect; its PhotoshootStatusListener was deleted in REF-6599)
    // or on studio detail page (StudioEditImageModal handles it locally)
    if (!isOnSamePhotoshoot && !isOnStudioDetailPage) {
      // REF-6306: beep now centralized in handleGenerationNotification — all design_types routed
      // here (tryon/ghost_shooting/photoshooting_mannequin/design_canvas_frame) are registry-covered.

      // Add action to navigate to the photoshoot if design_id is available
      const successOptions = message.design_id
        ? {
            action: {
              label: "View Photoshoot",
              onClick: () => {
                // Clear all snackbars before navigating
                context.dispatch(resetSnackbar());
                context.navigate(`/photoshoot/${message.design_id}`);
              },
            },
          }
        : {
            action: {
              label: "View Photoshoots",
              onClick: () => {
                // Clear all snackbars before navigating
                context.dispatch(resetSnackbar());
                context.navigate("/photoshoots");
              },
            },
          };

      context.showSuccess("Photoshoot is generated successfully.", successOptions);
    }
  }
};

const handleNotification = (message: SocketMessage, context: MessageHandlerContext) => {
  const notificationMessage = message.content || "New notification";
  const severityHandlers: Record<NotificationSeverity, (msg: string) => void> = {
    success: context.showSuccess,
    error: context.showError,
    warning: context.showWarning,
    info: context.showInfo,
  };

  const handler = severityHandlers[message.severity as NotificationSeverity] || context.showInfo;
  handler(notificationMessage);
};

const handleDesignStatusUpdate = (message: SocketMessage, context: MessageHandlerContext) => {
  const statusMessages: Record<string, { handler: (msg: string, options?: any) => void; message: string }> = {
    completed: { handler: context.showSuccess, message: "Design generation completed!" },
    failed: { handler: context.showError, message: "Design generation failed" },
  };

  const statusConfig = statusMessages[message.status as string];
  if (statusConfig) {
    statusConfig.handler(statusConfig.message);
  }
};

const handleTrainingProgress = (message: SocketMessage, context: MessageHandlerContext & { isMessageProcessed?: (id: string) => boolean; markMessageAsProcessed?: (id: string) => void }) => {
  // Handle error status (99)
  if (message.status === 99 || message.type === "failed") {
    if (!shouldProcessMessage(message, "training", context)) {
      return;
    }

    const errorMessage = getErrorMessage(message, "An error occurred during training");
    context.showError(errorMessage);
    reportSocketError(message, errorMessage, "training");

    return;
  }

  // Handle training success
  if (message.type === "success" && message.status === 1) {
    if (!shouldProcessMessage(message, "training", context)) {
      return;
    }

    // Only show notification if not on trainings page (TrainingStatusListener will handle it there)
    if (!location.pathname.includes("training")) {
      playSound();

      // Add action to navigate to the training if train_id is available
      const successOptions =
        message.train_id || message.training_id
          ? {
              action: {
                label: "View Training",
                onClick: () => {
                  // Clear all snackbars before navigating
                  context.dispatch(resetSnackbar());
                  const trainId = message.train_id || message.training_id;
                  context.navigate(`/trainings/${trainId}`);
                },
              },
            }
          : {
              action: {
                label: "View Trainings",
                onClick: () => {
                  // Clear all snackbars before navigating
                  context.dispatch(resetSnackbar());
                  context.navigate(PATHS.MOODBOARDS_MY);
                },
              },
            };

      context.showSuccess("Training completed successfully!", successOptions);
    }
  } else if (message.progress !== undefined) {
    // Handle progress updates
    if (process.env.NODE_ENV === "development") {
      console.log(`[SocketListener] Training progress: ${message.progress}%`);
    }
  }
};

const handleSessionExpired = (_message: SocketMessage, context: MessageHandlerContext) => {
  context.showWarning("Session expired. Please login again.");
  context.navigate("/login");
};

const handleMaintenanceMode = (_message: SocketMessage, context: MessageHandlerContext) => {
  context.showInfo("System is under maintenance. Please try again later.");
};

const handlePaymentRequired = (_message: SocketMessage, context: MessageHandlerContext) => {
  context.showWarning("This feature requires a paid plan");
};

const handleCollaborationUpdate = (message: SocketMessage, context: MessageHandlerContext) => {
  const actionMessages: Record<string, string> = {
    user_joined: `${message.userName} joined the workspace`,
    user_left: `${message.userName} left the workspace`,
  };

  const actionMessage = actionMessages[message.action as string];
  if (actionMessage) {
    context.showInfo(actionMessage);
  }
};

const handleErrorMessage = (message: SocketMessage, context: MessageHandlerContext) => {
  const errorMessage = getErrorMessage(message, "An error occurred");
  context.showError(errorMessage);
  reportSocketError(message, errorMessage, "unknown");
};

const handleGetVectorFile = (message: SocketMessage, context: MessageHandlerContext & { isMessageProcessed?: (id: string) => boolean; markMessageAsProcessed?: (id: string) => void }) => {
  const isFailure = message.status === 99 || message.type === "failed";
  // REF-6877 (U2): only a terminal frame acts, and only in the tab that started this request — it consumes the
  // entry, so a replay, another tab or a reloaded page does nothing. The progress frame (`success/0`) never
  // consumes it. The notification row / manual link is handled separately and is not gated.
  if (!isFailure && message.status !== 1) return;
  if (!consumeOwnedDownload("get_vector_file", message.design_id)) return;

  // Handle error status (99) or failed type. Once per job already: the consume above removed the entry
  // (REF-6928 dropped the prefix + client-timestamp guard here, which hid a second owned failure in the same ms).
  if (isFailure) {
    const errorMessage = getErrorMessage(message, "An error occurred during vector file conversion");
    context.showError(errorMessage);
    reportSocketError(message, errorMessage, "techpack");

    // Dispatch event to reset download state
    window.dispatchEvent(new CustomEvent(EventSvgDownloadComplete));

    return;
  }

  // Handle success case
  if (!message?.design_detail?.images[0]?.path) {
    context.showError("Vector file URL not provided");

    // Dispatch event to reset download state
    window.dispatchEvent(new CustomEvent(EventSvgDownloadComplete));

    return;
  }

  const downloadVectorFile = async () => {
    const vectorUrl = message?.design_detail?.images[0]?.path as string;

    // iOS/iPadOS: this fires on WS completion with no user activation, so the
    // fetch → blob → `<a download>` click can never trigger a download (REF-5796).
    // Best-effort: open the vector file in a new tab (may be popup-blocked without
    // a gesture — no worse than the silent no-op the anchor click was on iOS).
    if (isIosDevice()) {
      openInNewTab(vectorUrl);
      // REF-6306: beep now centralized in handleGenerationNotification (get_vector_file persist=True).
      context.showSuccess(`Vector file opened in a new tab: ${message.file_name || `vector_${Date.now()}.svg`}`);
      window.dispatchEvent(new CustomEvent(EventSvgDownloadComplete));

      return;
    }

    try {
      const response = await fetch(vectorUrl);

      if (!response.ok) {
        throw new Error(`Failed to download vector file: ${response.statusText}`);
      }

      const blob = await response.blob();

      if (blob.size === 0) {
        throw new Error("Downloaded vector file is empty");
      }

      const objectUrl = URL.createObjectURL(blob);
      const filename = message.file_name || `vector_${Date.now()}.svg`;

      const link = document.createElement("a");
      link.style.display = "none";
      link.href = objectUrl;
      link.download = filename;

      document.body.appendChild(link);
      link.click();

      URL.revokeObjectURL(objectUrl);
      document.body.removeChild(link);

      // REF-6306: beep now centralized in handleGenerationNotification (get_vector_file persist=True).
      context.showSuccess(`Vector file downloaded successfully: ${filename}`);

      // Dispatch event to notify that SVG download is complete
      window.dispatchEvent(new CustomEvent(EventSvgDownloadComplete));
    } catch (error) {
      console.error("Error downloading vector file", { status: httpStatusOf(error) });
      context.showError(`Failed to download vector file: ${error instanceof Error ? error.message : "Unknown error"}`);

      // Also dispatch event on error to reset download state
      window.dispatchEvent(new CustomEvent(EventSvgDownloadComplete));
    }
  };

  downloadVectorFile();
};

// "Download as 4K" completion. Mirrors handleGetVectorFile: the crystal-upscaler result
// arrives as design_type: "upscale_image" with the 4K image at design_detail.images[0].path.
// We fetch it, trigger the browser download, and dispatch EventUpscaleComplete so the
// "Enhancing resolution" popup resets on every surface. Only one upscale runs at a time
// (the menu blocks re-entry while downloading), same single-flight assumption as SVG.
const handleUpscaleImage = (message: SocketMessage, context: MessageHandlerContext & { isMessageProcessed?: (id: string) => boolean; markMessageAsProcessed?: (id: string) => void }) => {
  const isFailure = message.status === 99 || message.type === "failed";
  // REF-6877 (U2): as for SVG — act only in the tab that started this job (`design_id` = the download-4k
  // `job_id`, REF-6868), once. The notification row / manual link is not gated.
  if (!isFailure && message.status !== 1) return;
  if (!consumeOwnedDownload("upscale_image", message.design_id)) return;

  // Handle error status (99) or failed type — once per job via the consume above (REF-6928, as for SVG).
  if (isFailure) {
    const errorMessage = getErrorMessage(message, i18n.t("snackbar:upscale.error"));
    context.showError(errorMessage);
    reportSocketError(message, errorMessage, "design-canvas");
    trackEvent(EVENTS.DOWNLOAD_4K.FAILED, { surface: "websocket", resolution: "4k", reason: "ws_error" });

    // Reset the "Enhancing resolution" popup. No silent 2K downgrade — the user retries.
    window.dispatchEvent(new CustomEvent(EventUpscaleComplete));

    return;
  }

  // BE sends the 4K result as a flat image_url (mirrors the convert_to_sketch WS payload).
  const resultUrl = message?.image_url;
  if (!resultUrl) {
    context.showError(i18n.t("snackbar:upscale.no_url"));
    // REF-5737: a "success" WS with no image_url is a silent BE contract break — report it so
    // it doesn't just vanish behind the "no_url" snackbar.
    reportSocketError(message, "4K upscale WS completed without an image_url", "design-canvas");
    trackEvent(EVENTS.DOWNLOAD_4K.FAILED, { surface: "websocket", resolution: "4k", reason: "no_url" });
    window.dispatchEvent(new CustomEvent(EventUpscaleComplete));

    return;
  }

  const downloadUpscaledFile = async () => {
    // iOS/iPadOS: this fires on WS completion with no user activation, so the
    // fetch → blob → `<a download>` click can never save the 4K file (REF-5796).
    // Best-effort: open it in a new tab (may be popup-blocked without a gesture —
    // no worse than the silent no-op the anchor click was on iOS).
    if (isIosDevice()) {
      // iOS has no live user gesture at WS completion, so a save can't fire here (REF-5796). Hand
      // off to the global Ios4kDownloadPrompt: it pre-fetches the 4K image and shows a Save button
      // whose tap is a fresh gesture → native Save to Photos/Files. Dismiss the "Enhancing" popup.
      dispatchIos4kReady({ resultUrl: resultUrl as string, fileName: message.file_name });
      // REF-6306: beep now centralized in handleGenerationNotification (upscale_image persist=True).
      trackEvent(EVENTS.DOWNLOAD_4K.SUCCESS, { surface: "websocket", resolution: "4k" });
      window.dispatchEvent(new CustomEvent(EventUpscaleComplete));

      return;
    }

    try {
      const response = await fetch(resultUrl as string);

      if (!response.ok) {
        throw new Error(`Failed to download 4K image: ${response.statusText}`);
      }

      const blob = await response.blob();

      if (blob.size === 0) {
        throw new Error("Downloaded 4K image is empty");
      }

      const objectUrl = URL.createObjectURL(blob);
      // Name the file after its CDN URL (same as every other download via getFilenameFromUrl),
      // not a generic "image_4k_<ts>" — REF-5392 (PM: drop "image_4k" from the filename).
      const filename = message.file_name || getFilenameFromUrl(resultUrl as string, "png");

      const link = document.createElement("a");
      link.style.display = "none";
      link.href = objectUrl;
      link.download = filename;

      document.body.appendChild(link);
      link.click();

      URL.revokeObjectURL(objectUrl);
      document.body.removeChild(link);

      // REF-6306: beep now centralized in handleGenerationNotification (upscale_image persist=True).
      context.showSuccess(i18n.t("snackbar:upscale.success"));
      trackEvent(EVENTS.DOWNLOAD_4K.SUCCESS, { surface: "websocket", resolution: "4k" });

      window.dispatchEvent(new CustomEvent(EventUpscaleComplete));
    } catch (error) {
      console.error("Error downloading 4K image", { status: httpStatusOf(error) });
      context.showError(i18n.t("snackbar:upscale.error"));
      // REF-5737: fetching/saving the finished 4K file can fail (CDN 4xx, empty blob) after the
      // upscale itself succeeded — that failure was analytics-only, so report it to Sentry too.
      reportError(error, {
        source: "websocket",
        featureArea: "design-canvas",
        requestPath: "designs/download-4k",
        fingerprint: ["download-4k", "fetch-failed"],
        extras: { result_url: resultUrl, design_id: message.design_id, session_id: message.session_id },
      });
      trackEvent(EVENTS.DOWNLOAD_4K.FAILED, { surface: "websocket", resolution: "4k", reason: "fetch_failed" });

      window.dispatchEvent(new CustomEvent(EventUpscaleComplete));
    }
  };

  downloadUpscaledFile();
};

// Generation-completion Notifications (REF-6306): map the WS `notification` key straight into an
// INotificationItem and prepend it to the bell's Notifications tab, bumping the unread badge.
// Additive and independent of MESSAGE_HANDLERS — a terminal frame can carry both a `design_type` the
// dispatch table already handles AND this key. BE doesn't send timestamp fields on the WS frame, so
// they're filled with "now" (the feed refetch on next panel open reconciles the real values).
// REF-6539: side effects (unread badge, toast, sound) fire once per notification id. `prependNotification`
// itself is idempotent, but the counter/sound were not — a replayed frame (reconnect replay stream, or a
// WS+poll double delivery) carries a fresh client timestamp, so the message-hash dedup does not catch it.
const MAX_SEEN_NOTIFICATION_IDS = 500;
const seenNotificationIds = new Set<string>();
const rememberNotificationId = (id: string): boolean => {
  if (seenNotificationIds.has(id)) return false;
  seenNotificationIds.add(id);
  if (seenNotificationIds.size > MAX_SEEN_NOTIFICATION_IDS) {
    const oldest = seenNotificationIds.values().next().value as string | undefined;
    if (oldest !== undefined) seenNotificationIds.delete(oldest);
  }

  return true;
};

const handleGenerationNotification = (notification: NonNullable<SocketMessage["notification"]>, dispatch: ReturnType<typeof useAppDispatch>) => {
  const isFirstDelivery = rememberNotificationId(String(notification.id));
  const nowIso = new Date().toISOString();
  const item: INotificationItem = {
    id: notification.id,
    title_key: notification.title_key,
    params: notification.params,
    target: notification.target,
    status: notification.status,
    success_count: notification.success_count,
    failure_count: notification.failure_count,
    cover_url: notification.cover_url ?? null,
    seen_at: null,
    read_at: null,
    created_at: nowIso,
    last_event_at: nowIso,
    updated_at: nowIso,
  };

  dispatch(prependNotification(item));
  if (!isFirstDelivery) return; // replayed / duplicate delivery: row refreshed, no second badge/toast/beep
  // Read the live store value rather than a render snapshot — this runs outside React render.
  dispatch(setUnreadCount(store.getState().notifications.unreadCount + 1));

  // On the design agent's page the chat is where its work is reported: production's toast (and its beep) arrived before
  // the chat had the result, for a generation the chat itself shows (the product's call, 2026-10-20). The bell still
  // gets the item; the chat plays the beep when its own generation lands (`DesignCanvasAgentProvider`).
  const onAgentPage = window.location.pathname.startsWith(PATHS.DESIGN_CANVAS_AGENT);

  // Toast-worthy (REF-6306): also surface it in the top-right stacking toaster. Additive — the bell
  // feed above already got the item regardless of `surface`.
  if (notification.surface && !onAgentPage) {
    dispatch(pushToast(item));
  }

  // REF-6306: single choke point for the completion beep across every registry-covered design_type.
  // Per-feature `playSound()` call sites for these design_types were removed to avoid a double-beep —
  // see the "Known Traps" / handoff notes for the poll-fallback call sites that still play their own
  // beep (a poll-recovered completion carries no `notification` key, so it would otherwise beep never).
  if (notification.status === "completed" && !onAgentPage) {
    playSound();
  }
};

const handleConnectMessage = (message: SocketMessage, context: MessageHandlerContext) => {
  if (message.session_id && context.setSessionId) {
    context.setSessionId(message.session_id);
    if (process.env.NODE_ENV === "development") {
      console.log("[SocketListener] Session ID received and stored:", message.session_id);
    }
  }
};

const handleMagicSelectionMessage = () => {
  // Handled by DesignStudio mask listeners/useSocketFallback. Keep a no-op handler
  // here so it isn't logged as "Unhandled message type".
};

const handleChatbotChunkMessage = () => {
  // Handled inside DashboardChatbot via its SocketContext.lastMessage subscription.
  // No-op here so it isn't logged as "Unhandled message type".
};

/**
 * REF-6367 — a Studio Polish batch settled.
 *
 * The toast lives here, not on the project detail surface, for the same reason `handleFeedbackResolved`
 * does: a 100+ frame batch runs for minutes and the user is usually somewhere else by the time it lands.
 * On the batch's own detail page the banner already reports the outcome, so the toast would be a second
 * copy of it — hence the path check.
 *
 * Per-FRAME messages are deliberately silent: a 400-frame run would otherwise be 400 toasts. That is also
 * why the BE emits the settlement exactly once (single-winner DB guard), including for `partial` and
 * `cancelled`. The realtime layer still re-sends that one frame on replay / re-drain, so it is latched on
 * `batch_id` (REF-6878) to fire once per batch.
 *
 * The notification bell now consumes the BE's registry (`notifications:studio.polish_ready`, resolved
 * client-side by `WhatsNewMenu/notificationDisplay.ts`), so this snackbar is no longer the only signal —
 * it stays because it is the immediate one, while the bell is the durable record.
 */
const handleStudioPolishMessage = (message: SocketMessage, context: MessageHandlerContext) => {
  if (message.edit_type !== "polish_batch") return;
  // REF-6878: a replayed / re-drained settlement is the same batch — toast it once. Checked only after the edit_type
  // check, so the per-frame `polish_edits` frames (which carry the same batch_id) never consume it.
  if (message.batch_id && !claimOccurrence(`polish_batch:${message.batch_id}`)) return;

  const projectId = message.project_id;
  const isOnThisProjectDetail = Boolean(projectId && location.pathname.includes(`/photoshoots/studio/${projectId}`));
  if (isOnThisProjectDetail) return;

  const failed = message.batch_progress?.failed ?? 0;
  const completed = message.batch_progress?.completed ?? 0;
  const navigateAction = projectId
    ? {
        action: {
          label: i18n.t("photoshoot:studio_project_detail.polish.toast_view") as string,
          onClick: () => {
            context.dispatch(resetSnackbar());
            context.navigate(`/photoshoots/studio/${projectId}`);
          },
        },
      }
    : undefined;

  if (failed > 0) {
    context.showWarning(i18n.t("photoshoot:studio_project_detail.polish.done_partial", { done: completed, failed }) as string, navigateAction);

    return;
  }

  playSound();
  context.showSuccess(i18n.t("photoshoot:studio_project_detail.polish.done", { done: completed }) as string, navigateAction);
};

const handleStudioProjectMessage = () => {
  // Photoshoot Studio generation progress / completion (REF-6185). Handled by the two studio surfaces
  // via their own SocketContext.lastMessage subscriptions, because the only useful response is
  // "refetch this list / this project" and each owns its own fetcher. No-op here so it isn't logged as
  // "Unhandled message type".
  //
  // No toast on purpose: the BE already e-mails the user on completion, and an in-app "your photoshoot is
  // ready" notification is a product decision, not a side effect of this fix. If it's wanted, here is
  // where it belongs (see handleFeedbackResolved for why a cross-surface toast lives in this file).
};

/**
 * An operator resolved one of this user's image-feedback reports (REF-6045) and the
 * corrected render is already written back into their project.
 *
 * The TOAST lives here rather than on a surface because the push is operator-triggered
 * and can land at any moment — while the user is on the pricing page, in another
 * project, anywhere. It used to be dispatched from StudioProjectDetail, which meant
 * anyone not sitting on that exact project's detail page learned about the fix from
 * email only. REF-6103 made the feedback loop span four surfaces, at which point
 * per-surface toasts would have been four copies of this.
 *
 * The REFETCH deliberately stays per-surface: only the surface holding `object_id`
 * knows how to reload it, and it must not fire for someone else's project. Dedup is
 * already handled upstream by the messageHash guard, so this fires once.
 */
const handleFeedbackResolved = (message: SocketMessage, context: MessageHandlerContext) => {
  context.showSuccess(i18n.t("common:image_feedback.resolved_toast"));
};

// Message type to handler mapping
const MESSAGE_HANDLERS: Record<MessageType, (message: SocketMessage, context: MessageHandlerContext) => void> = {
  design: handleDesignMessage,
  notification: handleNotification,
  design_status_update: handleDesignStatusUpdate,
  training_progress: handleTrainingProgress,
  session_expired: handleSessionExpired,
  maintenance: handleMaintenanceMode,
  payment_required: handlePaymentRequired,
  collaboration_update: handleCollaborationUpdate,
  // REF-6928: the SVG / 4K results are handled per frame through `subscribe` (see OWNED_RESULT_HANDLERS), not
  // from `lastMessage`. No-op here so they aren't logged as "Unhandled message type"; their `notification` key is
  // still handled on the lastMessage path above, in every tab.
  get_vector_file: () => undefined,
  upscale_image: () => undefined,
  error: handleErrorMessage,
  mannequin_photoshoot: handlePhotoshootMessage,
  photoshooting_mannequin: handlePhotoshootMessage,
  ghost_shooting: handlePhotoshootMessage,
  tryon: handlePhotoshootMessage,
  design_canvas_frame: handlePhotoshootMessage,
  connected: handleConnectMessage,
  magic_selection: handleMagicSelectionMessage,
  chatbot_chunk: handleChatbotChunkMessage,
  feedback_resolved: handleFeedbackResolved,
  studio_project: handleStudioProjectMessage,
  studio_polish: handleStudioPolishMessage,
};

/**
 * REF-6928 (Phase D · D3a2): the SVG / 4K result handlers run per admitted frame, through the D2 `subscribe`
 * channel, instead of from `lastMessage`. `lastMessage` collapses a same-task burst to its last frame, so a result
 * followed in the same commit by any other frame was never handled: no download, and the 4K popup stayed open
 * until its 20-min watchdog. Ownership is unchanged (REF-6877): each handler acts only when it consumes this tab's
 * pending entry, so a frame can never be handled twice whichever path delivers it.
 */
const OWNED_RESULT_HANDLERS: Partial<Record<MessageType, (message: SocketMessage, context: MessageHandlerContext) => void>> = {
  get_vector_file: handleGetVectorFile,
  upscale_image: handleUpscaleImage,
};
const isOwnedResultFrame = (frame: SocketMessage | null | undefined): frame is SocketMessage => Boolean(frame?.design_type && OWNED_RESULT_HANDLERS[frame.design_type]);

// Survives the per-route remount of `SocketListener`, because `SocketContext.lastMessage` now does
// too (P1-1 hoisted the provider above `<Routes>`). One socket, one app, one consumption guard.
const lastProcessedMessageAcrossMounts: { current: string | null } = { current: null };

const SocketListener: React.FC<SocketListenerProps> = ({ children }) => {
  const socketContext = useContext(SocketContext);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showSuccess, showError, showInfo, showWarning } = useSnackbar();
  // P1-1: MODULE-scoped, not a ref. `SocketProvider` was hoisted above `<Routes>` so `lastMessage`
  // now SURVIVES a route change — but this component is still instantiated per route, and a fresh
  // ref meant the preserved frame was processed a second time on the next mount. That replays a
  // terminal generation event: duplicate notification rows, unread counts, toasts and completion
  // sounds. The guard has to outlive the component exactly as far as the message it guards does.
  const lastProcessedMessage = lastProcessedMessageAcrossMounts;

  const processSocketMessage = (message: SocketMessage) => {
    if (process.env.NODE_ENV === "development") {
      console.log("[SocketListener] Received message:", message);
    }

    // Additive: independent of the design_type/type dispatch below, and must run even when that
    // early-returns (no design_type/type on the frame) or when the type has no MESSAGE_HANDLERS entry.
    if (message.notification) {
      handleGenerationNotification(message.notification, dispatch);
    }

    // Use design_type if available, otherwise fall back to type for messages like "connect"
    const messageType = message.design_type || (message.type as MessageType);
    if (!messageType) {
      if (process.env.NODE_ENV === "development") {
        console.log("[SocketListener] Message missing design_type and type:", message);
      }

      return;
    }

    // If design_type is in DESIGN_TYPES_ARRAY, use "design" as the message type
    const effectiveMessageType = DESIGN_TYPES_ARRAY.includes(messageType as any) ? "design" : messageType;

    const handler = MESSAGE_HANDLERS[effectiveMessageType as MessageType];
    if (handler) {
      const context: MessageHandlerContext & { isMessageProcessed?: (id: string) => boolean; markMessageAsProcessed?: (id: string) => void } = {
        navigate,
        dispatch,
        showSuccess,
        showError,
        showInfo,
        showWarning,
        isMessageProcessed: socketContext?.isMessageProcessed,
        markMessageAsProcessed: socketContext?.markMessageAsProcessed,
        setSessionId: socketContext?.setSessionId,
        isDesignCompleted: socketContext?.isDesignCompleted,
      };
      handler(message, context);
    } else if (process.env.NODE_ENV === "development") {
      console.log(`[SocketListener] Unhandled message type: ${messageType}`, message);
    }
  };

  // The subscribe handler outlives renders; it reads the current snackbar / navigate callbacks through this ref.
  const ownedResultContext = useRef<MessageHandlerContext>({ navigate, dispatch, showSuccess, showError, showInfo, showWarning });
  ownedResultContext.current = { navigate, dispatch, showSuccess, showError, showInfo, showWarning };

  const subscribe = socketContext?.subscribe;
  const getRetained = socketContext?.getRetained;
  // Declared BEFORE the lastMessage effect: on mount it must see `lastProcessedMessage` as the previous mount left it.
  useEffect(() => {
    if (!subscribe) return;
    const handleOwnedResult = (frame: SocketMessage) => OWNED_RESULT_HANDLERS[frame.design_type as MessageType]?.(frame, ownedResultContext.current);

    // A result admitted while no SocketListener was mounted (a route without it) is still the retained frame:
    // hand it to the same consume-gated handler once. Skipped when the lastMessage path already saw that frame in an
    // earlier mount: it was then handled live by the subscription, or by that mount's own retained check.
    const retained = getRetained?.() as SocketMessage | null | undefined;
    if (isOwnedResultFrame(retained) && (retained as { messageHash?: string }).messageHash !== lastProcessedMessage.current) {
      handleOwnedResult(retained);
    }

    return subscribe(isOwnedResultFrame, handleOwnedResult);
  }, [subscribe, getRetained]);

  useEffect(() => {
    if (!socketContext?.lastMessage) return;

    const messageHash = socketContext.lastMessage.messageHash || JSON.stringify(socketContext.lastMessage);
    if (messageHash === lastProcessedMessage.current) return;

    lastProcessedMessage.current = messageHash;
    processSocketMessage(socketContext.lastMessage);
  }, [socketContext?.lastMessage]);

  return <>{children}</>;
};

export default SocketListener;
